'use client';
import { FC, Suspense, useEffect, useRef, useState } from 'react';
import {
  CatAnchorNavItem,
  CatButton,
  CatTable,
  CatTableBody,
  CatTableCell,
  CatTableHeader,
  CatTableHeaderCell,
  CatTableObject,
  CatTableRow,
  CatTextLink
} from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import {
  DealerPriceAvailabilityResponse,
  ReplacementParts
} from 'services/dealerPriceAvailability/types';
import { Conditional, useBreakpoint } from '@cat-ecom/pcc-components';
import { PAGE_BREAKPOINTS } from 'constants/common';
import PdpPartsMobileView from '../pdp-parts-mobile-view';
import CommonPartsTable from '../common-parts-table';
import { mergePriceAvailabilityResponse } from '../../utils/mergePriceAvailabilityRes';
import { DealerResponse } from 'services/dealer';
import { DealerData } from 'types/parts-diagram';
import ProductQuickViewDrawer from 'components/product-quick-view-drawer';
import { itemSummaryApiCall } from 'services/parts-diagram/actions';
import { FixedContainer } from 'app/[lang]/[esite]/(core)/product/[product]/fixed-container';
import AddToCart from 'components/actions/add-to-cart/add-to-cart';
import {
  MIN_REPLACED_PARTS,
  PCC_PDP_REPLACEMENTS_PARTS,
  RELATED_ID
} from '../../constants/constants';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';
import { CartResponse } from 'services/cart/types';
import cx from 'classnames';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { getFormattedSearchSelectItem } from 'components/product-comparison-grid/product-comparison-card/utils';
import styles from './product-replaced-parts-table.module.scss';

export interface ProductReplacedPartsTableProps {
  replacementParts: ReplacementParts[];
  locale: string;
  dealerData: DealerData;
  dealerInformation: DealerResponse;
  availRemoveInterimStockFlag: boolean;
  isShowSourcingDetailEnabled: boolean;
  isDeliveryEnabled: boolean;
  catcorp: boolean;
  priceData: DealerPriceAvailabilityResponse;
  isHeadlessPDPEnabled?: boolean;
  cartDetails: CartResponse;
  locationId?: string;
  isPccReleaseRemanReplacement?: boolean;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
}

const ProductReplacedPartsTable: FC<ProductReplacedPartsTableProps> = ({
  replacementParts,
  locale,
  dealerData,
  dealerInformation,
  availRemoveInterimStockFlag,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  catcorp,
  priceData,
  isHeadlessPDPEnabled,
  cartDetails,
  locationId,
  isPccReleaseRemanReplacement,
  isRTL = false,
  currencyLdFeatureFlag = false
}) => {
  const t = useTranslations();
  const [drawerActive, setDrawerActive] = useState(false);
  const [itemSummaryData, setItemSummaryData] = useState(null);
  const [replacedPartPrice, setReplacedPartPrice] = useState(null);
  const isDesktop = useBreakpoint(PAGE_BREAKPOINTS.BLOCKS_LG);
  const { currency = '', priceAndAvailability } = priceData || {};
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const hasViewListItem = useRef(false);

  const replacePriceAndAvailability = mergePriceAvailabilityResponse(
    replacementParts,
    priceAndAvailability
  );

  const handleOpen = async (partNumber: string, price) => {
    !catcorp && setReplacedPartPrice(price);
    const itemSummaryResponse = await itemSummaryApiCall(partNumber);
    setItemSummaryData(itemSummaryResponse);
    fireSelectItemEvent({
      item: getFormattedSearchSelectItem(cartDetails?.orderHeader, price),
      partName: price && `${price?.partNumber}: ${price?.name}`,
      locationId: locationId,
      itemSummary: price,
      isSearchPageEvent: true,
      itemListId: PCC_PDP_REPLACEMENTS_PARTS,
      itemListName: PCC_PDP_REPLACEMENTS_PARTS,
      serialNumber: '',
      fitment: ''
    });
  };

  useEffect(() => {
    const replaceViewItemList = {
      item_list_id: PCC_PDP_REPLACEMENTS_PARTS,
      item_list_name: PCC_PDP_REPLACEMENTS_PARTS
    };
    const viewReplacePayload = {
      products: replacePriceAndAvailability,
      itemListData: replaceViewItemList,
      cartItemsData: cartDetails,
      cartId: cartDetails?.orderHeader?.orderId,
      isProductsData: true
    };

    if (replacePriceAndAvailability?.length > 0 && !hasViewListItem.current) {
      fireViewItemListEvent(viewReplacePayload);
      hasViewListItem.current = true;
    }
  }, [cartDetails, fireViewItemListEvent, replacePriceAndAvailability]);

  return (
    <>
      {isDesktop ? (
        <CatTableObject
          height="auto"
          className={styles['replaced_table_objects']}
        >
          <CatTable
            /* @ts-expect-error Modifying a var from blocks to fix the default high z-index of the table header cell */
            style={{ '--cat-table-header-cell-zindex-pass-down': '10' }}
          >
            <CatTableHeader className={styles['table-header']}>
              <CatTableRow>
                <CatTableHeaderCell width="180px">
                  {t('CAT_CONTACT_YOUR_DEALER_PRODUCT_NUMBER')}
                </CatTableHeaderCell>
                <CatTableHeaderCell
                  className={styles['quantity-header']}
                  width="90px"
                >
                  {t('QUANTITY1')}
                </CatTableHeaderCell>
                <CatTableHeaderCell width="180px">
                  {t('PDP_AVAILBLE_FOR_PICKUP')}
                </CatTableHeaderCell>
                <CatTableHeaderCell align="right">
                  {t('TOTAL_PRICE')}
                </CatTableHeaderCell>
                <CatTableHeaderCell
                  align="right"
                  width="70px"
                ></CatTableHeaderCell>
              </CatTableRow>
            </CatTableHeader>
            <CatTableBody>
              {replacePriceAndAvailability?.map((item) => {
                const { partNumber = '', name = '', quantity } = item;
                return (
                  <CatTableRow key={partNumber}>
                    <CatTableCell>
                      <CatTextLink
                        className="cat-u-theme-typography-body"
                        onClick={() => {
                          setDrawerActive(true);
                          handleOpen(partNumber, item);
                        }}
                      >
                        {`${partNumber} : ${name}`}
                      </CatTextLink>
                    </CatTableCell>
                    <CatTableCell align="center">{quantity}</CatTableCell>
                    <CommonPartsTable
                      catcorp={catcorp}
                      item={item}
                      currency={currency}
                      locale={locale}
                      dealerData={dealerData}
                      dealerInformation={dealerInformation}
                      availRemoveInterimStockFlag={availRemoveInterimStockFlag}
                      isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
                      isDeliveryEnabled={isDeliveryEnabled}
                      isRTL={isRTL}
                      currencyLdFeatureFlag={currencyLdFeatureFlag}
                    />
                    <CatTableCell align="right">
                      <AddToCart
                        productPrice={item}
                        typeIcon={true}
                        productPriceData={item}
                        cartId={cartDetails?.orderHeader?.orderId}
                        locationId={locationId}
                        currency={currency}
                        itemlistIdName={PCC_PDP_REPLACEMENTS_PARTS}
                      />
                    </CatTableCell>
                  </CatTableRow>
                );
              })}
            </CatTableBody>
          </CatTable>
        </CatTableObject>
      ) : (
        <div className="cat-u-margin-top-xl">
          {!catcorp &&
            replacePriceAndAvailability?.map((replaceItem) => {
              return (
                <PdpPartsMobileView
                  key={replaceItem.partNumber}
                  remanItem={replaceItem}
                  enableAddToCart={replaceItem?.enableAddToCart}
                  locale={locale}
                  dealerData={dealerData}
                  dealerInformation={dealerInformation}
                  availRemoveInterimStockFlag={availRemoveInterimStockFlag}
                  isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
                  isDeliveryEnabled={isDeliveryEnabled}
                  currency={currency}
                  catcorp={catcorp}
                  replacementParts={replacementParts}
                  setDrawerActive={setDrawerActive}
                  handleOpen={handleOpen}
                  isPccReleaseRemanReplacement={isPccReleaseRemanReplacement}
                  isRTL={isRTL}
                  currencyLdFeatureFlag={currencyLdFeatureFlag}
                />
              );
            })}
        </div>
      )}
      {itemSummaryData && (
        <Suspense fallback={null}>
          <ProductQuickViewDrawer
            data={itemSummaryData}
            priceData={replacedPartPrice}
            dealerData={dealerData}
            currency={currency}
            drawerActive={drawerActive}
            setDrawerActive={setDrawerActive}
            isHeadlessPDPEnabled={isHeadlessPDPEnabled}
            isPccReleaseRemanReplacement={isPccReleaseRemanReplacement}
            isRTL={isRTL}
            currencyLdFeatureFlag={currencyLdFeatureFlag}
          />
        </Suspense>
      )}
      <Conditional
        test={
          replacePriceAndAvailability?.length == 1 &&
          isPccReleaseRemanReplacement &&
          !isDesktop
        }
      >
        {
          <FixedContainer
            spacerClassName="d-md-none"
            containerClassName="d-md-none"
          >
            <CatAnchorNavItem
              href={`#${RELATED_ID}`}
              className={styles['view-replcement-btn']}
            >
              <CatButton variant="contrast" size="md" fullWidth>
                {t('VIEW_REPLACEMENT')}
              </CatButton>
            </CatAnchorNavItem>
          </FixedContainer>
        }
      </Conditional>
      <Conditional
        test={
          !isDesktop &&
          isPccReleaseRemanReplacement &&
          replacePriceAndAvailability?.length > 1
        }
        fallback={
          replacePriceAndAvailability?.length >= MIN_REPLACED_PARTS && (
            <div className={cx('mt-3', styles['add-all-to-cart-btn'])}>
              <AddToCart
                productPrice={replacePriceAndAvailability}
                buttonLabel={true}
                fullWidth={false}
                productPriceData={replacePriceAndAvailability}
              />
            </div>
          )
        }
      >
        <FixedContainer
          spacerClassName="d-md-none"
          containerClassName="d-md-none"
        >
          <div className={cx('w-100', styles['add-all-to-cart-btn'])}>
            <AddToCart
              productPrice={replacePriceAndAvailability}
              buttonLabel={true}
              fullWidth={true}
              size="md"
            />
          </div>
        </FixedContainer>
      </Conditional>
    </>
  );
};

export default ProductReplacedPartsTable;
